'use client';

import { useEffect } from 'react';
import Link from 'next/link';

/**
 * Kaympus 404 — "this page is marked absent".
 *
 * Editorial one-screen layout on plain white: giant 404 → caption → links. The
 * three digits are hand-built SVG paths (not text) so the composition never
 * depends on a webfont loading, and each digit is clipped over a different
 * school surface:
 *
 *   4  chalkboard   — slate-green board with chalk geometry + tally marks
 *   0  brand teal   — Kaympus gradient ring with a pencil sitting in the counter
 *   4  ruled paper  — notebook rules, red margin, ink marks
 *
 * Rendered as `position: fixed` full-bleed on purpose. The root layout wraps
 * every route in ConditionalLayout, which puts the authenticated admin sidebar
 * and header around anything that isn't a whitelisted public path — and a 404
 * URL is by definition not on that list. Covering the viewport keeps that
 * chrome out of the way without having to special-case the guard for a route
 * that, by definition, doesn't exist.
 *
 * All styling is scoped, self-contained CSS (`.k404*`) rather than utilities:
 * `index.css` pins `body`/`h1..h6` to Roboto Serif app-wide and the Tailwind
 * config remaps `sans`/`serif`/`sora` to it as well, so a utility-built version
 * of this page would silently inherit the wrong face.
 */

/* Digit outlines, drawn in a local 230 × 440 box. Each is a single path with
   two subpaths (outer shape + counter) resolved with `evenodd`, so the counter
   punches a hole instead of being painted over — that's what lets the surface
   behind each digit show through the negative space. */
const FOUR = 'M145,0 L230,0 L230,440 L145,440 L145,340 L0,340 L0,250 Z M145,92 L145,258 L48,258 Z';
const ZERO =
  'M230,130 L230,310 A115,130 0 0 1 0,310 L0,130 A115,130 0 0 1 230,130 Z ' +
  'M160,150 L160,290 A45,45 0 0 1 70,290 L70,150 A45,45 0 0 1 160,150 Z';

/* Notebook rules for the third digit — every 38 units down its local box. */
const PAPER_RULES = Array.from({ length: 11 }, (_, i) => 52 + i * 38);

export default function NotFound404() {
  // The page is a fixed full-viewport surface; locking the document keeps the
  // admin shell underneath from scrolling behind it on wheel/touch.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div className="k404" data-testid="not-found-root">
      <style>{CSS}</style>

      <main className="k404__body" data-testid="not-found-body">
        <div className="k404__art" data-testid="not-found-art">
          <svg className="k404__svg" viewBox="0 0 780 460" role="img" aria-labelledby="k404-title" data-testid="not-found-illustration">
            <title id="k404-title">404</title>

            <defs>
              <clipPath id="k404-c1">
                <path d={FOUR} clipRule="evenodd" transform="translate(0,10)" />
              </clipPath>
              <clipPath id="k404-c2">
                <path d={ZERO} clipRule="evenodd" transform="translate(275,10)" />
              </clipPath>
              <clipPath id="k404-c3">
                <path d={FOUR} clipRule="evenodd" transform="translate(550,10)" />
              </clipPath>

              <linearGradient id="k404-teal" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#4F6AE4" />
                <stop offset="45%" stopColor="#3D5EE1" />
                <stop offset="100%" stopColor="#1A33A4" />
              </linearGradient>

              <radialGradient id="k404-board" cx="38%" cy="26%" r="82%">
                <stop offset="0%" stopColor="#1B35AC" />
                <stop offset="100%" stopColor="#102169" />
              </radialGradient>
            </defs>

            {/* ── Digit 1 — chalkboard ───────────────────────────────── */}
            <g clipPath="url(#k404-c1)">
              <g transform="translate(0,10)">
                <rect x="-10" y="-10" width="250" height="460" fill="url(#k404-board)" />

                {/* Half-wiped chalk dust: wide, very low-opacity sweeps. */}
                <g fill="none" stroke="#E8EBFB" strokeLinecap="round">
                  <path d="M-20,150 C50,118 118,188 250,138" strokeWidth="26" strokeOpacity="0.08" />
                  <path d="M-20,302 C64,274 146,326 250,288" strokeWidth="34" strokeOpacity="0.06" />
                </g>

                {/* Board work — geometry in the stem, tally marks on the bar. */}
                <g fill="none" stroke="#EBEEFC" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M162,58 L214,146 L162,146 Z" strokeWidth="3" strokeOpacity="0.5" />
                  <path d="M162,134 L174,134 L174,146" strokeWidth="2.5" strokeOpacity="0.38" />
                  <path d="M158,204 L220,204" strokeWidth="3" strokeDasharray="10 8" strokeOpacity="0.4" />
                  <circle cx="188" cy="278" r="24" strokeWidth="2.5" strokeOpacity="0.36" />
                  <path d="M188,278 L212,278" strokeWidth="2.5" strokeOpacity="0.36" />
                  <path d="M172,392 L206,392" strokeWidth="3" strokeOpacity="0.3" />

                  <g strokeWidth="4" strokeOpacity="0.52">
                    <path d="M28,280 L28,320" />
                    <path d="M46,280 L46,320" />
                    <path d="M64,280 L64,320" />
                    <path d="M82,280 L82,320" />
                    <path d="M20,324 L92,274" />
                  </g>
                </g>
              </g>
            </g>

            {/* ── Digit 2 — Kaympus teal, pencil in the counter ───────── */}
            <g clipPath="url(#k404-c2)">
              <g transform="translate(275,10)">
                <rect x="-10" y="-10" width="250" height="460" fill="url(#k404-teal)" />
                <ellipse cx="58" cy="74" rx="150" ry="122" fill="#FFFFFF" opacity="0.13" />
                <path
                  d="M6,338 A115,130 0 0 0 224,338"
                  fill="none"
                  stroke="#FFFFFF"
                  strokeOpacity="0.1"
                  strokeWidth="12"
                />
              </g>
            </g>
            <g transform="translate(275,10)">
              <path
                d={ZERO}
                fillRule="evenodd"
                fill="none"
                stroke="#FFFFFF"
                strokeOpacity="0.22"
                strokeWidth="2.5"
              />

              {/* Pencil — mirrors the counter's shape, tilts on hover. */}
              <g className="k404__pencil">
                <path d="M82,150 L82,126 A33,21 0 0 1 148,126 L148,150 Z" fill="#EE8C86" />
                <rect x="82" y="150" width="66" height="20" fill="#C9CED6" />
                <rect x="82" y="155" width="66" height="2.5" fill="#A5ADB8" />
                <rect x="82" y="162" width="66" height="2.5" fill="#A5ADB8" />
                <rect x="82" y="170" width="66" height="110" fill="#F3B740" />
                <rect x="82" y="170" width="13" height="110" fill="#DFA22C" opacity="0.7" />
                <rect x="136" y="170" width="12" height="110" fill="#FFFFFF" opacity="0.28" />
                <path d="M82,280 L148,280 L115,330 Z" fill="#E9C89B" />
                <path d="M82,280 L115,280 L115,330 Z" fill="#D6B084" />
                <path d="M99,305 L131,305 L115,330 Z" fill="#2E3436" />
              </g>
            </g>

            {/* ── Digit 3 — ruled notebook paper ─────────────────────── */}
            <g clipPath="url(#k404-c3)">
              <g transform="translate(550,10)">
                <rect x="-10" y="-10" width="250" height="460" fill="#FFFFFF" />
                {PAPER_RULES.map((y) => (
                  <rect key={y} x="-10" y={y} width="250" height="2" fill="#9DBBD6" opacity="0.9" />
                ))}
                <rect x="40" y="-10" width="2.5" height="460" fill="#E06C60" opacity="0.9" />

                {/* Ink on the page: a line of writing and a marked-correct tick. */}
                <path
                  d="M14,302 q16,-14 32,0 t32,0 t32,0 t26,0"
                  fill="none"
                  stroke="#3465B0"
                  strokeOpacity="0.75"
                  strokeWidth="4.5"
                  strokeLinecap="round"
                />
                <path
                  d="M166,298 L177,310 L203,278"
                  fill="none"
                  stroke="#DC5A4E"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </g>

            {/* Hairline outlines so every digit reads as one cut shape. The
                paper digit is the same value as the white page, so it carries a
                heavier line than the two dark surfaces. */}
            <g fill="none" stroke="#111A17" strokeWidth="2">
              <path d={FOUR} transform="translate(0,10)" strokeOpacity="0.14" />
              <path d={ZERO} transform="translate(275,10)" strokeOpacity="0.14" />
              <path d={FOUR} transform="translate(550,10)" strokeOpacity="0.34" strokeWidth="2.5" />
            </g>
          </svg>

          <div className="k404__stamp" aria-hidden="true" data-testid="not-found-stamp">
            <em>Absent</em>
            <small>No entry in the register</small>
          </div>
        </div>

        <h1 className="k404__sr" data-testid="not-found-title">404 — Page not found</h1>

        <p className="k404__caption" data-testid="not-found-caption">
          This page isn&rsquo;t on the timetable. It may have been moved, renamed &mdash; or it
          never enrolled.
        </p>

        <nav className="k404__links" data-testid="not-found-links">
          <Link href="/" className="k404__link k404__link--primary" data-testid="not-found-home-link">
            Return to homepage
          </Link>
          <span className="k404__linkSep">//</span>
          <Link href="/contact" className="k404__link" data-testid="not-found-contact-link">
            Contact the office
          </Link>
        </nav>
      </main>
    </div>
  );
}

const CSS = `
.k404 {
  position: fixed;
  inset: 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #FFFFFF;
  color: #111A17;
  font-family: Inter, 'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system,
    'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ── Body ───────────────────────────────────────────────────────────── */
.k404__body {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(16px, 2.6vh, 30px);
  padding: 24px 20px;
  text-align: center;
}

/* Width is capped three ways so the artwork also respects short viewports:
   the digits are 780 × 460, so a 46vh height ceiling means a 78vh width cap. */
.k404__art {
  position: relative;
  width: min(660px, 84vw, 78vh);
  flex: 0 0 auto;
}
.k404__svg {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
  filter: drop-shadow(0 14px 26px rgba(17, 26, 23, 0.11));
}
.k404__pencil {
  transform-box: fill-box;
  transform-origin: 50% 88%;
  transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
}
.k404__art:hover .k404__pencil {
  transform: rotate(-7deg);
}

/* ── Absent stamp ───────────────────────────────────────────────────── */
.k404__stamp {
  position: absolute;
  top: -7%;
  right: -3%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 16px 9px;
  border: 2.5px solid #C24236;
  border-radius: 7px;
  box-shadow: inset 0 0 0 2px #FFFFFF, inset 0 0 0 4px #C24236;
  color: #C24236;
  transform: rotate(8deg);
  opacity: 0.9;
  mix-blend-mode: multiply;
  pointer-events: none;
}
.k404__stamp em {
  font-style: normal;
  font-size: clamp(15px, 1.9vw, 24px);
  font-weight: 800;
  letter-spacing: 0.24em;
  text-indent: 0.24em;
  text-transform: uppercase;
  line-height: 1;
}
.k404__stamp small {
  font-size: clamp(6px, 0.62vw, 8px);
  font-weight: 700;
  letter-spacing: 0.2em;
  text-indent: 0.2em;
  text-transform: uppercase;
  line-height: 1;
  opacity: 0.85;
}

/* ── Copy ───────────────────────────────────────────────────────────── */
.k404__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
.k404__caption {
  max-width: 56ch;
  margin: 0;
  font-size: clamp(10.5px, 1vw, 13px);
  font-weight: 600;
  letter-spacing: 0.15em;
  line-height: 1.85;
  text-transform: uppercase;
  color: #33443E;
}
.k404__links {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.4vw, 18px);
  flex-wrap: wrap;
  justify-content: center;
}
.k404__link {
  padding-bottom: 3px;
  font-size: clamp(10.5px, 1vw, 13px);
  font-weight: 700;
  letter-spacing: 0.17em;
  text-transform: uppercase;
  text-decoration: none;
  color: #111A17;
  border-bottom: 2px solid currentColor;
  transition: color 0.25s ease, opacity 0.25s ease;
}
.k404__link:hover,
.k404__link:focus-visible {
  color: #2040CF;
}
.k404__link--primary {
  color: #1B35AC;
}
.k404__link--primary:hover,
.k404__link--primary:focus-visible {
  color: #111A17;
}
.k404__linkSep {
  font-size: clamp(10.5px, 1vw, 13px);
  font-weight: 700;
  color: rgba(17, 26, 23, 0.32);
}

/* ── Responsive / motion ────────────────────────────────────────────── */
@media (max-width: 520px) {
  .k404__stamp { display: none; }
  .k404__art { width: 88vw; }
  .k404__caption { letter-spacing: 0.12em; line-height: 1.75; }
}

@media (prefers-reduced-motion: reduce) {
  .k404__pencil { transition: none; }
}
`;
